import { useMutation } from '@tanstack/react-query';
import AgoraAdvertisement from 'components/agora-advertisement/agora-advertisement';
import CreateListingDialog from 'components/create-listing-dialog/create-listing-dialog';
import FeaturedListings from 'components/featured-listings';
import { MarkAsSoldDialog } from 'components/mark-as-sold-dialog';
import { MarketplaceEnquiryDialog } from 'components/marketplace-enquiry-dialog';
import { SendBroadcastDialog } from 'components/send-broadcast-dialog';
import {
  Button,
  Flex as CFlex,
  ErrorMessage,
  Grid,
  Icon,
  Loader,
  Stack,
  Text,
} from 'components/ui';
import UpgradeAgora from 'components/upgrade-agora/upgrade-agora';
import { SubscriptionGuard } from 'components/upgrade-subscription-dialog';
import { useBuyerOrg } from 'hooks/use-buyer-org';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { useUser } from 'hooks/use-user';
import { ApiClient } from 'lib/api/client';
import { ListingBookmark } from 'lib/api/generated';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import plur from 'plur';
import { FC, useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { toast } from 'react-toastify';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import { useStore } from '../../lib/store';
import { PATHS } from '../../settings/config';
import EOIList from './eoi-list';
import ListingAction from './listing-actions';

import ListingDetails from './listing-detail';
import { ListingDetailDialog } from './listing-detail-dialog';
import ListingDetailHeader from './listing-detail-header';
import SellerContactInfo from './seller-contact-info';

const ListingViewWrapper = styled.div`
  background: #ffffff;
`;
const BackButton = styled(Button)`
  margin: 0px;
  color: black;
  background: none;
  &:hover {
    color: black;
    background: none;
  }
  @media screen and (max-width: ${breakpoints.medium}) {
    margin: 0px;
    font-size: 24px;
    background-color: ${globalTheme.colors.blue};
    color: ${globalTheme.colors.white};
    &:hover {
      background-color: ${globalTheme.colors.blue};
      color: ${globalTheme.colors.white};
    }
  }
`;

const ContentWrapper = styled.div`
  margin: 40px 80px;
  margin-top: 0;
  @media screen and (max-width: ${breakpoints.small}) {
    margin: 40px 10px;
    margin-top: 0;
  }
`;
const ActionBannerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[2]};
  margin-bottom: ${globalTheme.space[3]};
  @media screen and (max-width: ${breakpoints.medium}) {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  padding: 20px;
  width: max-content;
  margin: auto;
  .request-callback-btn {
    background-color: ${globalTheme.colors.pink};
    &:hover {
      background-color: ${globalTheme.colors.lightPink};
      color: black;
    }
  }
  .send-offer-btn {
    background-color: black;
    &:hover {
      background-color: ${globalTheme.colors.stockbookBorder};
      color: black;
    }
  }
  @media screen and (max-width: ${breakpoints.medium}) {
    padding: 0px;
    margin-top: 15px;
  }

  @media screen and (min-width: ${breakpoints.xxLarge}) {
    display: none;
  }
`;

const ActionButton = styled(Button)`
  width: 100%;
  height: 44px;
`;

const BroadcastContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 12px;
  align-items: center;
  @media screen and (max-width: ${breakpoints.small}) {
    grid-template-columns: max-content 1fr;
    ${Icon} {
      grid-column: 1 / 2; /* Start at column 1 */
      grid-row: 1 / 2; /* Start at row 1 */
    }

    ${Text} {
      grid-column: 2 / 3;
      grid-row: 1 / 2;
    }

    ${Button} {
      font-size: ${globalTheme.fontSizes[3]};
      height: ${globalTheme.space[6]};
      border-radius: 100px;
      grid-column: 1 / 3;
      grid-row: 2 / 3;
    }
  }
`;

const HeaderButton = styled(CFlex)`
  padding: ${globalTheme.space[1]} 80px;
  @media screen and (max-width: ${breakpoints.small}) {
    padding: ${globalTheme.space[2]};
  }
`;

const EOIWrapper = styled.div`
  padding: ${globalTheme.space[0]} 80px;
  @media screen and (max-width: ${breakpoints.medium}) {
    display: none;
  }
`;

// Backend-generated SEO copy rendered below "Similar Listings". `body` is
// trusted, first-party HTML injected as-is, so this wrapper provides the
// typography for whatever headings/paragraphs/lists it contains.
const SeoContent = styled.section`
  margin-top: ${globalTheme.space[5]};
  padding-top: ${globalTheme.space[4]};
  border-top: 1px solid ${globalTheme.colors.stockbookBorder};
  line-height: 1.6;

  h1 {
    margin: 0 0 ${globalTheme.space[3]};
  }

  p {
    margin: 0 0 ${globalTheme.space[3]};
  }

  h2,
  h3 {
    margin: ${globalTheme.space[4]} 0 ${globalTheme.space[2]};
    font-weight: bold;
  }

  ul,
  ol {
    margin: 0 0 ${globalTheme.space[3]};
    padding-left: ${globalTheme.space[4]};
  }

  li {
    margin-bottom: ${globalTheme.space[1]};
  }

  a {
    color: ${globalTheme.colors.blue};
    text-decoration: underline;
  }
`;

export const PublicListingView: FC = ({
  listingDetails: data,
  listingErrors: error,
  publicListingData,
  refetch,
}: any) => {
  const router = useRouter();
  const [actionType, setActionType] = useState<
    'enquiry' | 'offer' | 'callback'
  >('enquiry');
  const { data: session } = useSession();
  const [openShareListing, setOpenShareListing] = useState<boolean>(false);
  const [openLoginDialog, setOpenLoginDialog] = useState<boolean>(false);
  const [listingDetails] = useState<any>(data);
  const [listingError] = useState<any>(error);
  const { data: org } = useBuyerOrg();
  const [markAsSoldDialogOpen, openMarkAsSoldDialog, closeMarkAsSoldDialog] =
    useDialogToggle(false);
  const [enquiryDialogOpen, openEnquiryDialog, closeEnquiryDialog] =
    useDialogToggle(false);
  const { data: user, isLoading: userLoading } = useUser();
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);
  const [
    sendBroadcastDialogOpen,
    openSendBroadcastDialog,
    closeSendBroadcastDialog,
  ] = useDialogToggle(false);
  const [
    createListingDialogOpen,
    openCreateListingDialog,
    closeCreateListingDialog,
  ] = useDialogToggle(false);

  const route = useStore((store) => store.routes);

  //handle back
  const handleBack = () => {
    if (route.length <= 1) {
      router.push(PATHS.marketplace.listings);
    } else {
      router.back();
    }
  };

  const bookmarkListingMutation = useMutation(
    (listingDetail: { listingId: string; listingBookmark: ListingBookmark }) =>
      new ApiClient().marketplacePublicListingApi.marketplaceListingBookmarkCreate(
        {
          listingId: listingDetail.listingId,
          listingBookmarkRequest: listingDetail.listingBookmark,
        }
      ),
    {
      onSuccess: () => {
        refetch();
        toast.success(`Status updated for ${listingDetails.title}`);
      },
      onError: () => {
        toast.error(`Failed to update status for ${listingDetails.title}`);
      },
    }
  );

  const updateOpenLoginDialog = (value: boolean) => {
    setOpenLoginDialog(value);
  };

  const updateShareListing = (value: boolean) => {
    setOpenShareListing(value);
  };

  const handleShareListing = () => {
    setOpenShareListing(true);
  };
  const handleMarkAsUnderOfferOrSold = () => {
    openMarkAsSoldDialog();
  };

  const handleCloseMarkAsSoldDialogClose = () => {
    closeMarkAsSoldDialog();
  };

  const updateSaleDetails = (value: any) => {
    // listingDetails.sold = value.sold;
    listingDetails.sold_price_type = value.sold_price_type;
    listingDetails.sold_price = value.sold_price;
    listingDetails.sold_hide_price = value.sold_hide_price;
    listingDetails.sold_buyer = value.sold_buyer;
  };

  if (!listingDetails && !listingError) {
    return (
      <div
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Loader size={5} />
      </div>
    );
  }

  if (listingError && !listingDetails) {
    return (
      <ErrorMessage>
        {listingError?.response?.status === 404
          ? 'Listing Not Found'
          : 'Something went wrong'}
      </ErrorMessage>
    );
  }

  const handleSendAction = (type: 'offer' | 'enquiry' | 'callback') => {
    setActionType(type);
    openEnquiryDialog();
  };

  const handleBookmarkChange = (
    listingId: string,
    bookmark: ListingBookmark
  ) => {
    bookmarkListingMutation.mutateAsync({
      listingId: listingId,
      listingBookmark: bookmark,
    });
  };

  return (
    <>
      <ListingViewWrapper>
        <HeaderButton
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <BackButton
            size='small'
            iconLeft='LeftArrowIcon'
            onClick={handleBack}
          >
            Back
          </BackButton>
          {isMobile && (
            <Button
              size='small'
              fontSize={4}
              variant='outline'
              type='button'
              iconLeft='ShareLinkIcon'
              onClick={handleShareListing}
            >
              Share
            </Button>
          )}
        </HeaderButton>
        {(publicListingData?.metadata?.is_owner ||
          (org?.is_admin && publicListingData?.metadata?.is_org_member)) && (
          <ActionBannerWrapper>
            <ListingDetailHeader
              listingDetails={publicListingData}
              handleShareListing={handleShareListing}
              handleMarkAsUnderOfferOrSold={handleMarkAsUnderOfferOrSold}
            />
            <ListingAction listingDetails={publicListingData} />

            <EOIWrapper>
              <Stack space={2}>
                {publicListingData.price_grid && (
                  <BroadcastContainer>
                    <Icon component='EyeIcon' size={2} />
                    <Text fontSize={2}>
                      {publicListingData.bookmark_count}{' '}
                      {plur('seller', publicListingData.bookmark_count)}{' '}
                      {plur('is', 'are', publicListingData.bookmark_count)}{' '}
                      watching this price.
                    </Text>
                    <SubscriptionGuard
                      featureName='ListingBroadcast'
                      action='create'
                      trigger={openSendBroadcastDialog}
                    >
                      {(canUpdate, trigger) => (
                        <Button
                          size='xSmall'
                          variant={canUpdate ? 'primary' : 'secondary'}
                          onClick={() => trigger()}
                        >
                          Send a broadcast
                        </Button>
                      )}
                    </SubscriptionGuard>
                  </BroadcastContainer>
                )}

                <Stack space={0}>
                  <Text as='h3' fontSize={4} fontWeight='medium'>
                    Expressions of Interest (EOIs)
                  </Text>
                  <EOIList listingId={publicListingData.id} />
                </Stack>
              </Stack>
            </EOIWrapper>
          </ActionBannerWrapper>
        )}

        <ContentWrapper>
          {/* Listing Detail Section */}
          <ListingDetails
            listingDetails={listingDetails}
            publicListingData={publicListingData}
            handleShareListing={handleShareListing}
            handleMarkAsSold={handleMarkAsUnderOfferOrSold}
            refetch={refetch}
            handleSendAction={handleSendAction}
          />

          {/* Action Button Section */}
          <ButtonContainer>
            {!listingDetails?.metadata?.is_org_member && (
              <Grid gap={0} columns={'1fr 1fr'}>
                <ActionButton
                  size='small'
                  variant='outline'
                  onClick={() => handleSendAction('enquiry')}
                >
                  Ask a question
                </ActionButton>

                <ActionButton
                  size='small'
                  className='send-offer-btn'
                  onClick={() => handleSendAction('offer')}
                >
                  Send an Offer
                </ActionButton>
              </Grid>
            )}
            <Grid
              gap={0}
              columns={
                !listingDetails?.metadata?.is_org_member
                  ? '1fr 1fr'
                  : 'max-width'
              }
              alignItems='center'
              justifyContent='center'
            >
              {!listingDetails?.metadata?.is_org_member && (
                <ActionButton
                  size='small'
                  className='request-callback-btn'
                  onClick={() => handleSendAction('callback')}
                >
                  Request Callback
                </ActionButton>
              )}

              <ActionButton
                size='small'
                variant='outline'
                type='button'
                onClick={handleShareListing}
              >
                <Icon size={2} component='PaperAirplaneIcon' />
                Share
              </ActionButton>
            </Grid>
          </ButtonContainer>

          {/* Seller Contact Info Section */}
          {session?.user?.user_id ? (
            session?.user?.user_id === publicListingData?.creator?.user_id ||
            session?.user?.user_id === listingDetails?.creator?.user_id ? ( // if session (one time) and creator id matches
              <></>
            ) : (
              <SellerContactInfo
                listingDetails={listingDetails || publicListingData}
                onCTABtnClick={handleSendAction}
              />
            )
          ) : (
            <UpgradeAgora />
          )}
          <FeaturedListings
            state={listingDetails?.listing_address?.state}
            product={listingDetails?.product}
            type={listingDetails.type}
          />

          {listingDetails?.metadata?.seo?.content?.h1 && (
            <SeoContent>
              <Text as='h1' fontSize={6} fontWeight='bold'>
                {listingDetails.metadata.seo.content.h1}
              </Text>
              {(listingDetails.metadata.seo.content.intro ||
                listingDetails.metadata.seo.content.body) && (
                <div
                  // intro is already wrapped in its own tag(s); render it and
                  // the body as-is in a single container.
                  dangerouslySetInnerHTML={{
                    __html: `${
                      listingDetails.metadata.seo.content.intro || ''
                    }${listingDetails.metadata.seo.content.body || ''}`,
                  }}
                />
              )}
            </SeoContent>
          )}
        </ContentWrapper>
        <AgoraAdvertisement />
      </ListingViewWrapper>
      {/* Dialog box */}
      <ListingDetailDialog
        updateShareListing={updateShareListing}
        openShareListing={openShareListing}
        updateOpenLoginDialog={updateOpenLoginDialog}
        openLoginDialog={openLoginDialog}
        listingDetails={listingDetails}
      />

      <MarkAsSoldDialog
        isOpen={markAsSoldDialogOpen}
        closeMarkAsSoldDialog={handleCloseMarkAsSoldDialogClose}
        listingDetails={publicListingData}
        updateSaleDetails={updateSaleDetails}
        refetch={refetch}
      />
      <MarketplaceEnquiryDialog
        type={actionType}
        openEnquiryDialog={enquiryDialogOpen}
        closeEnquiryDialog={closeEnquiryDialog}
      />
      <SendBroadcastDialog
        listingId={listingDetails.id}
        isOpen={sendBroadcastDialogOpen}
        onDismiss={closeSendBroadcastDialog}
      />
      <CreateListingDialog
        isOpen={createListingDialogOpen}
        onDismiss={closeCreateListingDialog}
      />
    </>
  );
};
